import { ApolloClient, useApolloClient } from '@apollo/client';
import { Checkbox, DatePicker, Input, Icon } from '@perch-labs/shrike';
import { useMachine } from '@xstate/react';
import { ReactElement, ComponentProps, HTMLInputTypeAttribute } from 'react';
import { SpaceProps } from 'styled-system';
import { DialogSelector } from '~/components/dialogs';
import { Container } from '~/components/layout';
import { Link } from '~/components/link';
import { IS_TEST } from '~/utils/constants';
import { getStyledSystemProps, removeStyledSystemProps } from '~/utils/styled-system';
import { validatorMachine } from './validating-input.machine';
import { InputWithIconWrapper, Requirements, StyledInput } from './validating-input.style';

type ShrikeInputTypeAttribute = Extract<
  HTMLInputTypeAttribute,
  'password' | 'number' | 'text' | 'tel'
>;
export interface ValidatingInputProps extends InputProps, SpaceProps {
  debounceMessage?: number;
  debounceValidation?: number;
  fieldName?: string;
  onChange: OnChange;
  optional?: boolean;
  requirements?: string;
  validate: Validator;
  type?: HTMLInputTypeAttribute;
  value?: string | boolean;
  helpContent?: {
    type: string;
    content?: any;
    copy?: Record<string, any>;
  };
}

export const ValidatingInput = ({
  id,
  debounceMessage = IS_TEST ? 1 : 750,
  debounceValidation = IS_TEST ? 1 : 250,
  fieldName,
  label,
  name,
  onChange,
  optional,
  requirements,
  validate,
  value,
  type,
  children,
  helpContent,
  ...props
}: ValidatingInputProps) => {
  const apollo = useApolloClient();
  const getFieldDefaultValue = () => {
    switch (type) {
      case 'checkbox':
        return false;
      default:
        return '';
    }
  };

  const [{ context, matches }, send] = useMachine(validatorMachine, {
    context: {
      apollo,
      fieldName: fieldName || name,
      value: value || getFieldDefaultValue(),
      onChange,
      optional,
      validate,
    },
    delays: { debounceMessage, debounceValidation },
  });

  switch (type) {
    case 'checkbox':
      return (
        <Container {...getStyledSystemProps(props)}>
          <Checkbox
            id={id}
            name={name}
            margin="16px 0"
            data-testid={`validating-input-checkbox-${name}`}
            checked={!!context.value}
            onChange={e => send({ type: 'CHANGE', value: e.target.checked })}
          >
            {children}
          </Checkbox>
        </Container>
      );
    case 'date': {
      const getDate = (value: any) => {
        if (!value || value === '') return null;
        return new Date(value);
      };
      return (
        <Container {...getStyledSystemProps(props)}>
          <DatePicker
            {...removeStyledSystemProps(props)}
            label={label}
            handleChange={date => {
              return send({ type: 'CHANGE', value: date?.toISOString().split('T')[0] as string });
            }}
            inputProps={{
              errorText: matches('invalid.showing') ? (context.message as string) : '',
            }}
            reactDatePickerProps={{
              dateFormat: 'MM/dd/yyyy',
              maxDate: new Date(),
              showYearDropdown: true,
              yearDropdownItemNumber: 90,
              scrollableYearDropdown: true,
              placeholderText: 'mm/dd/yyyy',
              strictParsing: true,
            }}
            invalidDateErrorMessage="Please enter a valid date of format mm/dd/yyyy"
            value={getDate(context.value)! as Date}
          />
          {!!requirements && (
            <Requirements
              className={matches('valid') ? 'valid' : 'invalid'}
              isValid={matches('valid')}
            >
              {requirements}
            </Requirements>
          )}
        </Container>
      );
    }
    default: {
      return (
        <Container {...getStyledSystemProps(props)}>
          <InputWithIconWrapper hasIcon={!!helpContent}>
            <StyledInput
              {...removeStyledSystemProps(props)}
              errorText={matches('invalid.showing') ? context.message : ''}
              label={label}
              name={name}
              onBlur={() => send({ type: 'BLUR' })}
              onFocus={() => send({ type: 'FOCUS' })}
              onChange={value => send({ type: 'CHANGE', value })}
              value={context.value as string}
              type={type as ShrikeInputTypeAttribute}
            />
            {!!helpContent && (
              <div className="help-icon">
                <DialogSelector
                  type={helpContent.type}
                  target={
                    <Link color="primary" aria-label="Help dialog">
                      <Icon icon="QuestionCircleInverted" />
                    </Link>
                  }
                  copy={{
                    fieldName: name,
                    fieldLabel: label,
                    ...helpContent.copy,
                  }}
                />
              </div>
            )}
          </InputWithIconWrapper>
          {!!requirements && (
            <Requirements
              className={matches('valid') ? 'valid' : 'invalid'}
              isValid={matches('valid')}
            >
              {requirements}
            </Requirements>
          )}
        </Container>
      );
    }
  }
};

export interface InputComponentProps
  extends SpaceProps,
    Omit<ValidatingInputProps, 'name' | 'label' | 'placeholder' | 'validate'> {}

export interface LabeledInputProps extends SpaceProps, Omit<ValidatingInputProps, 'validate'> {
  name: string;
  label: string;
}

export type OnChangeMeta = { name: string; isValid: boolean; optional?: boolean };

type InputProps = Omit<ComponentProps<typeof Input>, 'onChange' | 'margin' | 'type' | 'value'>;

type ValidationResponse = boolean | string | ReactElement;

type Validator = (options: {
  apollo?: ApolloClient<{}>;
  value: string | boolean;
}) => ValidationResponse | Promise<ValidationResponse>;

export type OnChange<TValue extends any = string> = (value: TValue, params: OnChangeMeta) => void;
